/**
 * Extension Sync Service
 *
 * Service for syncing data between web-app and Chrome extension via localStorage events
 */

import type { ProfileResponse } from './profile.service';

interface ExtensionMessage {
  type: string;
  data?: any;
  timestamp: number;
}

class ExtensionSyncService {
  private readonly SYNC_KEY = 'extension-sync';
  private readonly RESPONSE_KEY = 'extension-sync-response';

  constructor() {
    // Listen for responses from extension
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.handleStorageEvent.bind(this));
    }
  }

  /**
   * Check if extension API is available (for direct access)
   */
  isExtensionAvailable(): boolean {
    return !!(typeof window !== 'undefined' && window.chrome?.runtime?.sendMessage);
  }

  /**
   * Send profile to extension via localStorage event
   */
  async syncProfileToExtension(profile: ProfileResponse): Promise<boolean> {
    console.log('🔄 [ExtensionSync] Starting profile sync via localStorage events...');

    try {
      // Convert profile to extension format
      const extensionProfile = {
        id: profile.id,
        fullName: `${profile.firstName} ${profile.lastName}`.trim(),
        email: profile.email,
        title: (profile.experience && profile.experience[0]) ? (profile.experience[0] as any).position || (profile.experience[0] as any).title : undefined,
        summary: profile.summary || undefined,
        skills: Array.isArray(profile.skills) ? profile.skills : [],
        experience: profile.experience?.map((exp: any) => ({
          title: exp.position || exp.title || '',
          company: exp.company || '',
          startDate: exp.startDate || '',
          endDate: exp.endDate || '',
          current: exp.current || false,
          description: exp.description || '',
        })) || [],
        education: profile.education?.map((edu: any) => ({
          degree: edu.degree || '',
          institution: edu.institution || '',
          field: edu.fieldOfStudy || edu.field || '',
          startDate: edu.startDate || '',
          endDate: edu.endDate || edu.graduationDate || '',
        })) || [],
      };

      const message: ExtensionMessage = {
        type: 'UPDATE_PROFILE',
        data: extensionProfile,
        timestamp: Date.now(),
      };

      console.log('📤 [ExtensionSync] Sending profile via localStorage:', extensionProfile.fullName);

      // Send via localStorage event
      localStorage.setItem(this.SYNC_KEY, JSON.stringify(message));

      // Clean up after a short delay
      setTimeout(() => {
        localStorage.removeItem(this.SYNC_KEY);
      }, 1000);

      console.log('✅ [ExtensionSync] Profile sent to extension via localStorage event');
      return true;

    } catch (error) {
      console.error('❌ [ExtensionSync] Error syncing profile:', error);
      return false;
    }
  }

  /**
   * Send profile deletion command to extension
   */
  async syncProfileDeletionToExtension(): Promise<boolean> {
    console.log('🗑️ [ExtensionSync] Starting profile deletion sync via localStorage events...');

    try {
      const message: ExtensionMessage = {
        type: 'DELETE_PROFILE',
        data: null,
        timestamp: Date.now(),
      };

      console.log('📤 [ExtensionSync] Sending profile deletion via localStorage');

      // Send via localStorage event
      localStorage.setItem(this.SYNC_KEY, JSON.stringify(message));

      // Clean up after a short delay
      setTimeout(() => {
        localStorage.removeItem(this.SYNC_KEY);
      }, 1000);

      console.log('✅ [ExtensionSync] Profile deletion sent to extension via localStorage event');
      return true;

    } catch (error) {
      console.error('❌ [ExtensionSync] Error syncing profile deletion:', error);
      return false;
    }
  }

  /**
   * Get profile from extension
   */
  async getProfileFromExtension(): Promise<any | null> {
    console.log('🔍 [ExtensionSync] Requesting profile from extension...');

    try {
      const message: ExtensionMessage = {
        type: 'GET_STATE',
        timestamp: Date.now(),
      };

      // Send request via localStorage
      localStorage.setItem(this.SYNC_KEY, JSON.stringify(message));

      // Wait for response (polling approach)
      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          console.log('⏰ [ExtensionSync] Timeout waiting for extension response');
          resolve(null);
        }, 5000);

        const checkResponse = () => {
          const responseStr = localStorage.getItem(this.RESPONSE_KEY);
          if (responseStr) {
            try {
              const response = JSON.parse(responseStr);
              if (response.timestamp > message.timestamp && response.type === 'GET_STATE_RESPONSE') {
                clearTimeout(timeout);
                localStorage.removeItem(this.RESPONSE_KEY);

                if (response.success && response.data?.userProfile) {
                  console.log('✅ [ExtensionSync] Profile retrieved from extension');
                  resolve(response.data.userProfile);
                } else {
                  console.log('❌ [ExtensionSync] No profile in extension');
                  resolve(null);
                }
                return;
              }
            } catch (error) {
              console.error('❌ [ExtensionSync] Error parsing extension response:', error);
            }
          }

          // Check again after a short delay
          setTimeout(checkResponse, 100);
        };

        // Start checking for response
        setTimeout(checkResponse, 100);
      });

    } catch (error) {
      console.error('❌ [ExtensionSync] Error getting profile from extension:', error);
      return null;
    }
  }

  /**
   * Handle storage events from extension
   */
  private handleStorageEvent(event: StorageEvent): void {
    if (event.key === this.SYNC_KEY && event.newValue) {
      console.log('📨 [ExtensionSync] Received storage event:', event.newValue);

      try {
        const message: ExtensionMessage = JSON.parse(event.newValue);
        console.log('📋 [ExtensionSync] Message type:', message.type, 'data:', message.data ? 'PRESENT' : 'NULL');
      } catch (error) {
        console.error('❌ [ExtensionSync] Error parsing storage event:', error);
      }
    }
  }
}

export const extensionSyncService = new ExtensionSyncService();